/**
 * Admin Brand Drilldown — scoped to URL :brand_id.
 *
 * Backend currently exposes admin-list endpoints only at the platform
 * scope (no /api/admin/brands/{id}/branches etc), so this drilldown
 * focuses on what's available:
 *   - Overview tab (analytics via /api/admin/brands/{id}/overview +
 *     brand info + primary owner)
 *   - Audit Log tab (admin/audit-logs?brand_id=)
 *
 * EX-B Step 6 — the analytics block in the Overview tab is rendered
 * through the shared <BrandAnalyticsPanel /> component so it matches
 * the Owner Overview surface 1:1. Brand isolation is preserved by
 * keying the query on brand_id + range and re-running
 * assertBrandMatch() on the response.
 *
 * Mandate 1 / 2:
 *   - All query keys carry brand_id as the first element of the tuple,
 *     so navigating between brands triggers cache invalidation and a
 *     guaranteed refetch.
 *   - assertBrandMatch() is run on the per-brand info card AND on the
 *     analytics response.
 */
import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Building2, ScrollText, ImageIcon, UserCheck } from 'lucide-react';
import { api, resolveImageUrl } from '@/lib/api';
import { qk, matchByBrand } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { assertBrandMatch, formatDateTime, formatRupiah } from '@/lib/utils';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, StatusPill, EmptyState,
} from '@/components/ui_primitives/Misc';
import Button from '@/components/ui_primitives/Button';
import { JsonViewer } from '@/components/ui_primitives/Misc';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui_primitives/Table';
import BrandAnalyticsPanel from '@/components/BrandAnalyticsPanel';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'crm', label: 'Customer CRM' },
  { id: 'retention', label: 'Retention' },
  { id: 'audit', label: 'Audit' },
];

export default function AdminBrandDrilldownPage() {
  const { brand_id: brandId } = useParams();
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  // EX-B Step 6 — per-brand analytics window state. Lives at the page
  // level so a brand switch resets it cleanly (the whole page unmounts).
  const [range, setRange] = useState('7d');

  // When the active brand changes, invalidate any cache holding the
  // *previous* brand's drilldown data so we never accidentally render
  // stale brand context (Mandate 2 — cache isolation).
  useEffect(() => {
    qc.invalidateQueries({ predicate: matchByBrand(brandId) });
  }, [brandId, qc]);

  const brandQ = useQuery({
    queryKey: qk.adminBrand(brandId),
    queryFn: async () => {
      const list = await api.adminBrands(token);
      const b = (list || []).find((x) => x.id === brandId);
      if (!b) throw { code: 'not_found', detail: 'Brand not found' };
      return assertBrandMatch(b, brandId, 'id');
    },
    enabled: !!token && !!brandId,
  });

  // EX-B Step 6 — analytics query: only fires when the Overview tab is
  // mounted (the audit tab doesn't need it). Response is asserted to
  // belong to the URL brand_id before reaching the panel.
  const overviewQ = useQuery({
    queryKey: qk.adminBrandOverview(brandId, range),
    queryFn: async () =>
      assertBrandMatch(await api.adminBrandOverview(token, brandId, range), brandId),
    enabled: !!token && !!brandId && tab === 'overview',
  });

  const auditQ = useQuery({
    queryKey: qk.adminBrandAudit(brandId),
    queryFn: () => api.adminAuditLogs(token, { brand_id: brandId, limit: 100 }),
    enabled: !!token && !!brandId && tab === 'audit',
  });

  if (brandQ.isLoading) return <FullSpinner />;
  if (brandQ.error) return <QueryError error={brandQ.error} onRetry={() => brandQ.refetch()} />;
  const b = brandQ.data;

  // Refresh = re-run the analytics aggregation (advances generated_at).
  const handleAnalyticsRefresh = () => {
    qc.invalidateQueries({ queryKey: qk.adminBrandOverview(brandId, range) });
  };

  return (
    <div className="space-y-6" data-testid="admin-brand-drilldown">
      <div className="flex items-center gap-2">
        <Link to="/admin/brands">
          <Button variant="ghost" size="sm" leftIcon={<ArrowLeft size={12} />}>All brands</Button>
        </Link>
      </div>

      <PageHeader
        kicker={`Brand · ${b.slug}`}
        title={b.name}
        subtitle={b.description}
        actions={b.is_active ? <StatusPill variant="gold">Active</StatusPill> : <StatusPill variant="slate">Inactive</StatusPill>}
      />

      {/* Brand banner */}
      <SectionCard padded={false} testID="brand-banner">
        <div className="relative aspect-[5/1] bg-black/40 rounded-2xl overflow-hidden">
          {b.banner_url ? (
            <img src={resolveImageUrl(b.banner_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 grid place-items-center text-white/30"><ImageIcon /></div>
          )}
          <div className="absolute bottom-4 left-4 flex items-center gap-3">
            {b.logo_url ? (
              <img src={resolveImageUrl(b.logo_url)} alt="" className="w-14 h-14 rounded-2xl border border-white/10 shadow-2xl" />
            ) : null}
            <div>
              <div className="font-display text-2xl font-bold text-white">{b.name}</div>
              <div className="text-xs text-white/60 font-mono">{b.slug}</div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-white/8" data-testid="brand-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button"
            onClick={() => {
              if (t.id === 'crm') {
                navigate(`/admin/brands/${brandId}/crm`);
              } else if (t.id === 'retention') {
                navigate(`/admin/brands/${brandId}/retention`);
              } else {
                setTab(t.id);
              }
            }}
            data-testid={`brand-tab-${t.id}`}
            className={
              'px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ' +
              (tab === t.id
                ? 'border-gold text-gold'
                : 'border-transparent text-white/55 hover:text-white')
            }
          >
            {t.id === 'crm' ? (
              <span className="inline-flex items-center gap-1.5">
                <UserCheck size={12} /> {t.label}
              </span>
            ) : t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? (
        <OverviewTab
          brand={b}
          overviewQ={overviewQ}
          range={range}
          onRangeChange={setRange}
          onAnalyticsRefresh={handleAnalyticsRefresh}
        />
      ) : null}
      {tab === 'audit' ? <AuditTab auditQ={auditQ} /> : null}
    </div>
  );
}

function OverviewTab({ brand, overviewQ, range, onRangeChange, onAnalyticsRefresh }) {
  return (
    <div className="space-y-8">
      {/* EX-B Step 6 — shared analytics panel, fed by /api/admin/brands/{id}/overview */}
      {overviewQ.isLoading ? (
        <FullSpinner />
      ) : overviewQ.error ? (
        <QueryError error={overviewQ.error} onRetry={() => overviewQ.refetch()} />
      ) : (
        <BrandAnalyticsPanel
          data={overviewQ.data}
          range={range}
          onRangeChange={onRangeChange}
          onRefresh={onAnalyticsRefresh}
          isLoading={overviewQ.isLoading}
          isFetching={overviewQ.isFetching}
        />
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <SectionCard className="lg:col-span-2 space-y-4" testID="brand-info">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Building2 size={16} className="text-gold" />Brand details</h3>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Row k="Slug" v={brand.slug} />
            <Row k="Status" v={brand.is_active ? 'Active' : 'Inactive'} />
            <Row k="Primary color" v={
              <span className="inline-flex items-center gap-2">
                <span className="w-4 h-4 rounded border border-white/15" style={{ background: brand.primary_color }} />
                <span className="font-mono">{brand.primary_color}</span>
              </span>
            } />
            <Row k="Stamp threshold" v={formatRupiah(brand.stamp_threshold_rupiah)} />
            <Row k="Stamps per card" v={brand.stamps_per_card ?? 10} />
            <Row k="Branches" v={brand.branch_count ?? 0} />
            <Row k="Active customers" v={(brand.active_customers ?? 0).toLocaleString('id-ID')} />
            <Row k="Created" v={brand.created_at ? formatDateTime(brand.created_at) : '—'} />
          </dl>
        </SectionCard>

        <SectionCard testID="brand-owner">
          <h3 className="font-display text-lg font-semibold mb-3">Primary owner</h3>
          {brand.owner ? (
            <div>
              <div className="text-base font-medium">{brand.owner.name}</div>
              <div className="text-xs font-mono text-white/55 mt-1">{brand.owner.email}</div>
              <div className="mt-3">
                {brand.owner.is_active === false ? (
                  <StatusPill variant="slate">Inactive</StatusPill>
                ) : (
                  <StatusPill variant="gold">Active</StatusPill>
                )}
              </div>
              <Link to="/admin/owners">
                <Button variant="subtle" size="sm" className="mt-4 w-full">Manage in Owners →</Button>
              </Link>
            </div>
          ) : <div className="text-sm text-white/40">No owner attached.</div>}
        </SectionCard>
      </div>
    </div>
  );
}

function AuditTab({ auditQ }) {
  if (auditQ.isLoading) return <FullSpinner />;
  if (auditQ.error) return <QueryError error={auditQ.error} onRetry={() => auditQ.refetch()} />;
  const rows = Array.isArray(auditQ.data) ? auditQ.data : (auditQ.data?.items || []);
  if (!rows.length) {
    return <EmptyState icon={<ScrollText size={20} />} title="No audit events for this brand yet."
      subtitle="Activity will appear here as soon as the owner or the brand's cashiers perform actions." />;
  }
  return (
    <SectionCard padded={false} testID="brand-audit">
      <Table>
        <THead>
          <Tr>
            <Th>Action</Th>
            <Th>Target</Th>
            <Th>Actor</Th>
            <Th>When</Th>
            <Th>Metadata</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((r) => (
            <Tr key={r.id}>
              <Td><span className="font-mono text-xs text-gold">{r.action}</span></Td>
              <Td><span className="text-xs">{r.target_type}<span className="text-white/30">·</span><span className="font-mono">{(r.target_id || '').slice(0, 8)}</span></span></Td>
              <Td><span className="text-xs font-mono text-white/70">{r.actor_email || '—'}</span></Td>
              <Td><span className="text-xs text-white/60">{formatDateTime(r.created_at)}</span></Td>
              <Td><div className="max-w-md"><JsonViewer data={r.metadata || {}} /></div></Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </SectionCard>
  );
}

function Row({ k, v }) {
  return (
    <>
      <dt className="text-white/45 text-xs uppercase tracking-wider">{k}</dt>
      <dd className="text-white">{v}</dd>
    </>
  );
}
